import { DatePipe, KeyValuePipe, NgClass } from "@angular/common";
import { ChangeDetectorRef, Component, ElementRef, inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ContentService } from "@bequeathed/ngx-services";
import { LoggerService } from '@bequeathed/ngx-app-core';
import { FormControlPipe, ThreadUtils } from "@bequeathed/ngx-utils";
import { QuestionUtils } from "../../../utils/question.utils";
import { BaseInputControl } from "../base-input-control/base-input-control.component";
import { FormControlInfoComponent, InfoMessage } from "../form-control-info/form-control-info.component";

const LOG: LoggerService = LoggerService.get('VideoControl');

export enum VideoStage {
    START,
    LOADING,
    RECORDING,
    PAUSED,
    SAVING,
    SAVED,
    FAILED_TO_RECORD,
    FAILED_TO_UPLOAD,
    ERRORED_WHILE_SAVING,
    COMPLETE
}

/**
 * Control to display video capture
 *
 * @author Dan Bennett (dbennett)
 */
@Component({
    selector: 'video-control',
    templateUrl: './video-control.component.html',
    styleUrls: [
        './video-control.component.sass',
        '../base-input-control/base-input-control.component.sass',
        '../../base-form/base-form.component.sass'
    ],
    standalone: true,
    imports: [
        // Angular
        FormsModule,
        NgClass,
        ReactiveFormsModule,
        // Bequeathed
        DatePipe,
        FormControlInfoComponent,
        FormControlPipe,
        KeyValuePipe,
    ],
    encapsulation: ViewEncapsulation.None
})
export class VideoControl extends BaseInputControl implements OnInit {

    changeDetection = inject(ChangeDetectorRef)
    contentService: ContentService = inject(ContentService);

    interviewId: number | undefined;

    @ViewChild('recordedVideo') recordVideoElementRef: ElementRef | undefined;
    @ViewChild('video') videoElementRef: ElementRef | undefined;

    timeLeft = 60;
    interval: any;

    videoElement: HTMLVideoElement | undefined;
    recordVideoElement: HTMLVideoElement | undefined;
    mediaRecorder: any;
    stream: MediaStream | undefined;
    recordedBlobs: Blob[] | undefined;
    textMode: boolean | undefined;

    stage: VideoStage = VideoStage.START;

    infoMessages: InfoMessage[] = [
        {
            code: 'WILL.help.video.saving',
            type: 'query',
            match: () => this.stage === VideoStage.SAVING
        },
        {
            code: 'WILL.help.video.paused',
            type: 'warning',
            match: () => this.stage === VideoStage.PAUSED
        },
        {
            code: 'WILL.help.video.errored',
            type: 'error',
            match: () => this.stage === VideoStage.ERRORED_WHILE_SAVING
        },
        {
            code: 'WILL.help.video.failed-to-start',
            type: 'error',
            match: () => this.stage === VideoStage.FAILED_TO_RECORD
        },
        {
            code: 'WILL.help.video.failed-to-upload',
            type: 'error',
            match: () => this.stage === VideoStage.FAILED_TO_UPLOAD
        },
        {
            code: 'WILL.help.video.saved',
            type: 'success',
            match: () => this.stage === VideoStage.COMPLETE
        },
        {
            code: 'error.required.video',
            type: 'error',
            match: () => !this.stage && this.controlHasError() && !this.textMode
        }
    ]

    constructor() {
        super();
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.determineValidators();

        this.textMode = !this.baseForm.interviewState()?.metadata?.recordingConsented;
        this.interviewId = this.baseForm.interviewState()?.interviewId;
    }

    async setupVideo(): Promise<void> {
        LOG.debug('setupVideo', 'Setting up for video capture...');
        const constraints = {
            audio: true,
            video: {
                width: 360,
            },
        };

        const stream: MediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        this.stream = new MediaStream(stream);
        this.videoElement = this.videoElementRef!.nativeElement;
        this.videoElement!.srcObject = this.stream;
        this.videoElement!.muted = true;
        this.recordVideoElement = this.recordVideoElementRef!.nativeElement;
        LOG.debug('setupVideo', 'Setting up complete');
    }

    getSupportedMimeTypes(): string[] {
        return [
            'video/webm;codecs=av1,opus',
            'video/webm;codecs=vp9,opus',
            'video/webm;codecs=vp8,opus',
            'video/webm;codecs=h264,opus',
            'video/mp4;codecs=h264,aac',
            'video/mp4'
        ]
            .filter(mimeType => MediaRecorder.isTypeSupported(mimeType));
    }

    async startRecording(): Promise<void> {

        this.stage = VideoStage.LOADING;
        LOG.debug('startRecording', 'Video capture loading...');
        this.baseForm.errorMap.clear();
        this.control.markAllAsTouched();
        this.control.setValue(null);
        this.recordedBlobs = [];
        this.timeLeft = 60;

        try {
            await this.setupVideo();

            this.mediaRecorder = new MediaRecorder(this.stream!);
            this.stage = VideoStage.RECORDING;
            LOG.debug('startRecording', 'Video capture recording...');

            this.preventSave.emit(true);
            this.mediaRecorder.start();
            this.startTimer();

            // Bind recording events
            this.mediaRecorder.ondataavailable = (event: any) => this.onDataAvailable(event);
            this.mediaRecorder.onstop = () => { this.onStopRecordingEvent(); };

        } catch (err) {
            LOG.error(err as any);
            this.stage = VideoStage.FAILED_TO_RECORD;
        }
    }

    stopRecording() {
        LOG.debug('stopRecording', 'Video capture stopped');
        this.mediaRecorder.stop();
        this.stopTimer();

        this.stage = VideoStage.SAVING;
        LOG.debug('stopRecording', `Recorded Blobs: ${this.recordedBlobs}`);
    }

    playPauseRecording() {
        if (this.stage !== VideoStage.PAUSED) {
            LOG.debug('playPauseRecording', 'Video capture paused');
            this.stage = VideoStage.PAUSED;
            this.mediaRecorder.pause();
            this.pauseTimer();
        } else {
            LOG.debug('playPauseRecording', 'Video capture resumed');
            this.stage = VideoStage.RECORDING;
            this.mediaRecorder.resume();
            this.startTimer();
        }
    }

    onDataAvailable(event: any) {
        if (event?.data?.size) {
            LOG.trace('onDataAvailable', 'Data stream blob available, capturing...');
            this.recordedBlobs!.push(event.data);
        }
    }

    onStopRecordingEvent() {
        this.stage = VideoStage.SAVING;
        const videoBuffer = new Blob(this.recordedBlobs, {
            type: this.getSupportedMimeTypes()[0],
        });
        this.recordVideoElement!.src = window.URL.createObjectURL(videoBuffer);

        this.loading.emit(true);
        this.saveDocument(videoBuffer).then(response => {
            if (response) {
                LOG.debug('onStopRecordingEvent', 'Video saved and transcribed successfully.');
                this.stage = VideoStage.COMPLETE;
                this.control.setValue(response);
                this.setModelValue(response)
                this.preventSave.emit(false);
            } else if (typeof response === 'string' && response === '') {
                this.stage = VideoStage.COMPLETE;
                this.control.setValue("[no audio recorded]");
                this.setModelValue(response)
                this.preventSave.emit(false);
            } else {
                LOG.error(`[onStopRecordingEvent] Error trying to save and transcribe video \n${response}`);
                this.stage = VideoStage.FAILED_TO_UPLOAD;
                this.control.setValue("[no audio recorded]");
                this.preventSave.emit(true);
            }
            this.loading.emit(false);
            this.control.clearValidators();

            const state = this.baseForm.interviewState();
            state!.currentQuestion!.userResponse = this.control.value;
            this.baseForm.interviewState.set(null);
            this.changeDetection.detectChanges();
            this.baseForm.interviewState.set(state);
            QuestionUtils.setUserMetadata(this.baseForm.interviewState()?.metadata)
        });

        this.mediaRecorder = undefined;
        this.stream!.getTracks().forEach((track) => {
            if (track.readyState == 'live') {
                track.stop();
            }
        });
    }

    async saveDocument(blob: any) {
        LOG.debug('saveDocument', 'Saving video...');
        const name = this.question.id!.replace(/\./g, '_') + '.' + this.getRecordingFileType(blob.type);
        const content = new File([blob], name, { type: blob.type })
        LOG.debug('saveDocument', 'Transcribing video...')
        const res: any = await this.contentService.startTranscription(this.interviewId!, this.getSupportedMimeTypes()[0], name, content);
        if (!res || res.errored) {
            LOG.debug('saveDocument', `Failed to start transcription: "${name}"`);
            this.stage = VideoStage.FAILED_TO_UPLOAD;
            this.loading.emit(false);
        } else {
            const transcription: string = await this.waitForTranscription(res);
            this.loading.emit(false);
            LOG.debug('saveDocument', `Video transcribed: "${transcription}"`);
            return transcription;
        }
        return null;
    }

    async waitForTranscription(res: { job: string, location: string }): Promise<string> {
        const result: string | null = await this.contentService.getTranscription(this.interviewId!, res.job, res.location);
        if (result === 'Transcription in progress...') {
            // Wait 2 seconds to reduce pressure on back-end
            await ThreadUtils.sleep(2000);
            return await this.waitForTranscription(res);
        }
        return result!;
    }

    blobToBase64(blob: any): Promise<any> {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        return new Promise((resolve, _) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    }

    startTimer() {
        this.interval = setInterval(() => {
            if (this.timeLeft > 0) {
                this.timeLeft--;
                this.changeDetection.detectChanges();
            } else {
                this.stopRecording();
            }
        }, 1000)
    }

    pauseTimer() {
        clearInterval(this.interval);
    }

    stopTimer() {
        clearInterval(this.interval);
        this.timeLeft = 60;
    }

    getRecordingFileType(mimeType: string): string {
        if (mimeType.startsWith('video/mp4')) {
            return 'mp4';
        }
        return 'webm';
    }

    public readonly VideoStage = VideoStage;

    async toggleVideoMode() {
        this.textMode = !this.textMode;
        if (this.textMode) {
            this.baseForm.interviewState()!.currentQuestion!.edited = true;
        }
        if (this.stage === VideoStage.PAUSED) {
            this.stage = VideoStage.START;
            this.mediaRecorder.terminate();
        }
        this.preventSave.emit(false);
    }
}
